import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Plus, DollarSign, CheckCircle } from "lucide-react";

import {
  Box,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Chip,
} from "@mui/material";

import DataTable from "@/components/mui/DataTable";

export default function Finance() {
  const [accounts, setAccounts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    type: "pagar",
    description: "",
    amount: 0,
    due_date: new Date().toISOString().split("T")[0],
    supplier_id: "",
    customer_id: "",
    quantidade_parcelas: 1,
  });

  // Paginação client-side
  const [page, setPage] = useState(0); // 0-based
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    fetchAccounts();
    fetchSuppliers();
    fetchCustomers();
  }, []);

  const fetchAccounts = async () => {
    try {
      const response = await axios.get(`${API}/finance`);
      const data = response.data.data || response.data;
      setAccounts(data);
    } catch (error) {
      toast.error("Erro ao carregar contas");
    } finally {
      setLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API}/suppliers`, { params: { per_page: 1000 } });
      setSuppliers(response.data.data || response.data);
    } catch (error) {
      console.log("Erro ao carregar fornecedores");
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`, { params: { per_page: 1000 } });
      setCustomers(response.data.data || response.data);
    } catch (error) {
      console.log("Erro ao carregar clientes");
    }
  };

  const formatMoney = (value) =>
    `R$ ${Number(value || 0).toFixed(2).replace(".", ",")}`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Se for conta a pagar, usa endpoint de payables
      if (formData.type === "pagar") {
        if (!formData.supplier_id) {
          toast.error("Selecione um fornecedor");
          return;
        }
        await axios.post(`${API}/payables`, {
          supplier_id: formData.supplier_id,
          description: formData.description,
          amount: formData.amount,
          due_date: formData.due_date,
          quantidade_parcelas: formData.quantidade_parcelas,
        });
        toast.success(`Conta a pagar criada com ${formData.quantidade_parcelas} parcela(s)!`);
      } else {
        // Conta a receber
        await axios.post(`${API}/receivables`, {
          customer_id: formData.customer_id || null,
          description: formData.description,
          amount: formData.amount,
          due_date: formData.due_date,
        });
        toast.success("Conta a receber criada!");
      }
      setShowModal(false);
      resetForm();
      fetchAccounts();
    } catch (error) {
      toast.error(error.response?.data?.message || "Erro ao criar conta");
    }
  };

  const markAsPaid = async (accountId) => {
    try {
      await axios.put(`${API}/finance/${accountId}/pay`);
      toast.success("Conta marcada como paga!");
      fetchAccounts();
    } catch (error) {
      toast.error("Erro ao marcar como paga");
    }
  };

  const resetForm = () => {
    setFormData({
      type: "pagar",
      description: "",
      amount: 0,
      due_date: new Date().toISOString().split("T")[0],
      supplier_id: "",
      customer_id: "",
      quantidade_parcelas: 1,
    });
  };

  const calculateTotal = (type, paid) => {
    return accounts
      .filter((acc) => acc.type === type && acc.paid === paid)
      .reduce((sum, acc) => sum + Number(acc.amount || 0), 0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage); // 0-based
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: 200,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const paginatedAccounts =
    rowsPerPage > 0
      ? accounts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : accounts;

  // ====== COLUMNS PARA O DataTable ======
  const columns = [
    {
      id: "type",
      label: "Tipo",
      render: (account) => (
        <Chip
          size="small"
          color={account.type === "receber" ? "success" : "warning"}
          label={account.type === "receber" ? "A Receber" : "A Pagar"}
        />
      ),
    },
    {
      id: "entity",
      label: "Fornecedor/Cliente",
      render: (account) => (
        <Typography fontSize={14}>
          {account.supplier?.name || account.customer?.name || "-"}
        </Typography>
      ),
    },
    {
      id: "description",
      label: "Descrição",
      render: (account) => (
        <Typography fontWeight={600}>{account.description}</Typography>
      ),
    },
    {
      id: "amount",
      label: "Valor",
      align: "right",
      render: (account) => (
        <Typography
          sx={{
            fontWeight: 600,
            color: account.type === "receber" ? "#10b981" : "#ef4444",
          }}
        >
          {formatMoney(account.amount)}
        </Typography>
      ),
    },
    {
      id: "parcelas",
      label: "Parcelas",
      render: (account) => (
        <Typography fontSize={14}>
          {account.installment_number 
            ? `${account.installment_number}/${account.total_installments}`
            : account.total_installments > 1 
              ? `${account.total_installments}x`
              : "1x"
          }
        </Typography>
      ),
    },
    {
      id: "due_date",
      label: "Vencimento",
      render: (account) => {
        const overdue =
          !account.paid && account.status !== 'received' && account.status !== 'paid' && new Date(account.due_date) < new Date();
        return (
          <Typography
            sx={{
              color: overdue ? "error.main" : "inherit",
              fontSize: 14,
            }}
          >
            {new Date(account.due_date).toLocaleDateString("pt-BR")}
            {overdue && (
              <Typography
                component="span"
                sx={{
                  ml: 1,
                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                (VENCIDO)
              </Typography>
            )}
          </Typography>
        );
      },
    },
    {
      id: "status",
      label: "Status",
      render: (account) => {
        const isPaid = account.paid || account.status === 'paid' || account.status === 'received';
        return (
          <Chip
            size="small"
            color={isPaid ? "success" : "error"}
            label={isPaid ? "Pago" : "Pendente"}
          />
        );
      },
    },
    {
      id: "actions",
      label: "Ações",
      render: (account) => {
        const isPaid = account.paid || account.status === 'paid' || account.status === 'received';
        return !isPaid ? (
          <Button
            variant="contained"
            color="success"
            size="small"
            startIcon={<CheckCircle size={14} />}
            onClick={() => markAsPaid(account.id)}
            data-testid={`pay-finance-${account.id}`}
          >
            Pagar
          </Button>
        ) : null;
      },
    },
  ];

  const tablePagination = {
    page, // 0-based
    rowsPerPage,
    total: accounts.length,
    onPageChange: handleChangePage,
    onRowsPerPageChange: handleChangeRowsPerPage,
    rowsPerPageOptions: [5, 10, 15, 25, 50],
  };

  return (
    <Box data-testid="finance-page">
      {/* Cards de resumo */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, minmax(0, 1fr))",
            md: "repeat(4, minmax(0, 1fr))",
          },
          gap: 3,
          mb: 3,
        }}
      >
        <Paper
          sx={{
            p: 3,
            minHeight: 140,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <Typography
            variant="subtitle1"
            color="text.secondary"
            sx={{ fontWeight: 500, mb: 1 }}
          >
            Total a Receber
          </Typography>
          <Typography
            variant="h4"
            sx={{
              color: "#10b981",
              fontWeight: 700,
              fontSize: { xs: "1.8rem", md: "2.1rem" },
            }}
            data-testid="total-receivable"
          >
            {formatMoney(calculateTotal("receber", false))}
          </Typography>
        </Paper>

        <Paper
          sx={{
            p: 3,
            minHeight: 140,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <Typography
            variant="subtitle1"
            color="text.secondary"
            sx={{ fontWeight: 500, mb: 1 }}
          >
            Total a Pagar
          </Typography>
          <Typography
            variant="h4"
            sx={{
              color: "#ef4444",
              fontWeight: 700,
              fontSize: { xs: "1.8rem", md: "2.1rem" },
            }}
            data-testid="total-payable"
          >
            {formatMoney(calculateTotal("pagar", false))}
          </Typography>
        </Paper>

        <Paper
          sx={{
            p: 3,
            minHeight: 140,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <Typography
            variant="subtitle1"
            color="text.secondary"
            sx={{ fontWeight: 500, mb: 1 }}
          >
            Recebido
          </Typography>
          <Typography
            variant="h4"
            sx={{
              color: "#0ea5e9",
              fontWeight: 700,
              fontSize: { xs: "1.8rem", md: "2.1rem" },
            }}
            data-testid="total-received"
          >
            {formatMoney(calculateTotal("receber", true))}
          </Typography>
        </Paper>

        <Paper
          sx={{
            p: 3,
            minHeight: 140,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <Typography
            variant="subtitle1"
            color="text.secondary"
            sx={{ fontWeight: 500, mb: 1 }}
          >
            Pago
          </Typography>
          <Typography
            variant="h4"
            sx={{
              color: "#0ea5e9",
              fontWeight: 700,
              fontSize: { xs: "1.8rem", md: "2.1rem" },
            }}
            data-testid="total-paid"
          >
            {formatMoney(calculateTotal("pagar", true))}
          </Typography>
        </Paper>
      </Box>

      {/* Tabela + header */}
      <Paper className="data-table-container">
        <Box
          className="table-header"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid",
            borderColor: "divider",
            px: 3,
            py: 2,
          }}
        >
          <Typography className="table-title" variant="h6">
            Contas Financeiras
          </Typography>

          <Button
            variant="contained"
            startIcon={<Plus size={16} />}
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            data-testid="add-finance-btn"
          >
            Nova Conta
          </Button>
        </Box>

        {accounts.length === 0 ? (
          <Box
            sx={{
              py: 6,
              px: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              gap: 1.5,
            }}
          >
            <DollarSign size={64} />
            <Typography variant="h6">
              Nenhuma conta registrada
            </Typography>
            <Typography variant="body2">
              Registre contas a pagar e a receber
            </Typography>
          </Box>
        ) : (
          <DataTable
            columns={columns}
            rows={paginatedAccounts}
            pagination={tablePagination}
            getRowId={(row) => row.id}
            getRowTestId={(row) => `finance-row-${row.id}`}
          />
        )}
      </Paper>

      {/* Dialog Nova Conta */}
      <Dialog
        open={showModal}
        onClose={() => {
          setShowModal(false);
          resetForm();
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Nova Conta</DialogTitle>
        <DialogContent dividers>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {/* Tipo */}
              <FormControl fullWidth size="small">
                <InputLabel id="finance-type-label">Tipo *</InputLabel>
                <Select
                  labelId="finance-type-label"
                  label="Tipo"
                  value={formData.type}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      type: e.target.value,
                      supplier_id: "",
                      customer_id: "",
                    })
                  }
                  required
                  data-testid="finance-type-select"
                >
                  <MenuItem value="pagar">A Pagar</MenuItem>
                  <MenuItem value="receber">A Receber</MenuItem>
                </Select>
              </FormControl>

              {/* Fornecedor (apenas para A Pagar) */}
              {formData.type === "pagar" && (
                <FormControl fullWidth size="small" required>
                  <InputLabel id="finance-supplier-label">Fornecedor *</InputLabel>
                  <Select
                    labelId="finance-supplier-label"
                    label="Fornecedor *"
                    value={formData.supplier_id}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        supplier_id: e.target.value,
                      })
                    }
                    data-testid="finance-supplier-select"
                  >
                    {suppliers.map((supplier) => (
                      <MenuItem key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {/* Cliente (apenas para A Receber) */}
              {formData.type === "receber" && (
                <FormControl fullWidth size="small">
                  <InputLabel id="finance-customer-label">Cliente</InputLabel>
                  <Select
                    labelId="finance-customer-label"
                    label="Cliente"
                    value={formData.customer_id}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        customer_id: e.target.value,
                      })
                    }
                    data-testid="finance-customer-select"
                  >
                    <MenuItem value="">Nenhum</MenuItem>
                    {customers.map((customer) => (
                      <MenuItem key={customer.id} value={customer.id}>
                        {customer.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {/* Descrição */}
              <TextField
                label="Descrição"
                size="small"
                fullWidth
                required
                value={formData.description}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    description: e.target.value,
                  })
                }
                data-testid="finance-description-input"
              />

              {/* Valor */}
              <TextField
                label="Valor Total (R$)"
                type="number"
                size="small"
                fullWidth
                inputProps={{ step: "0.01", min: "0.01" }}
                required
                value={formData.amount}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    amount: parseFloat(e.target.value) || 0,
                  })
                }
                data-testid="finance-amount-input"
              />

              {/* Quantidade de Parcelas (apenas para A Pagar) */}
              {formData.type === "pagar" && (
                <TextField
                  label="Quantidade de Parcelas"
                  type="number"
                  size="small"
                  fullWidth
                  inputProps={{ min: 1, max: 120 }}
                  value={formData.quantidade_parcelas}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      quantidade_parcelas: parseInt(e.target.value) || 1,
                    })
                  }
                  helperText={
                    formData.quantidade_parcelas > 1 && formData.amount > 0
                      ? `${formData.quantidade_parcelas}x de R$ ${(formData.amount / formData.quantidade_parcelas).toFixed(2).replace(".", ",")}`
                      : "1 = pagamento único"
                  }
                  data-testid="finance-parcelas-input"
                />
              )}

              {/* Vencimento */}
              <TextField
                label={formData.quantidade_parcelas > 1 ? "Data 1ª Parcela" : "Data de Vencimento"}
                type="date"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                required
                value={formData.due_date}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    due_date: e.target.value,
                  })
                }
                helperText={formData.quantidade_parcelas > 1 ? "As demais parcelas serão mensais" : ""}
                data-testid="finance-duedate-input"
              />
            </Box>

            <DialogActions sx={{ mt: 3 }}>
              <Button
                type="button"
                variant="outlined"
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                data-testid="cancel-finance-btn"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="contained"
                data-testid="submit-finance-btn"
              >
                Criar
              </Button>
            </DialogActions>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
